import React from "react";
import ContentPage from "../components/ContentPage";

export default function ProblemPage() {
  return (
    <ContentPage title="The Problem We Solve">
      <div className="prose prose-invert max-w-none" data-testid="problem-content">
        <p className="text-[#E8E4DC] text-lg mb-8">
          In today's hyper-connected world, we're drowning in windows, tabs, and notifications. 
          The tools meant to help us multitask are actually making us <em>less</em> productive.
        </p>

        <h2 className="font-heading text-2xl font-semibold text-white mt-10 mb-4">The Pain Is Real</h2>
        <div className="space-y-4 mb-8">
          {[
            { title: "Window Juggling", desc: "The average knowledge worker switches between apps 1,200 times per day. Each switch costs 23 minutes of refocus time (University of California, Irvine). That's hours of lost productivity every single day.", stat: "1,200 switches/day" },
            { title: "Missed Moments", desc: "You're on a Zoom call and need to take notes. You Alt-Tab to your document and miss a critical slide. You check your stocks and miss the meeting recap. You follow a tutorial and lose the video every time you switch to your code editor.", stat: "23 min to refocus" },
            { title: "Limited Native PiP", desc: "Built-in Picture-in-Picture only works with video elements on specific websites. You can't PiP a dashboard, a document, a chat window, or a live stream from a tab that doesn't support it. And you certainly can't customize the frame or size.", stat: "Video only" },
            { title: "No Cross-Platform Solution", desc: "Mac has Stage Manager. Windows has Snap Layouts. Chrome has basic PiP. None of them talk to each other, none of them give you an always-on-top overlay, and none let you customize the experience.", stat: "0 unified tools" },
            { title: "Team Blind Spots", desc: "In trading floors, NOCs, and war rooms, teams need shared always-visible displays. Existing solutions require expensive hardware. There's no software-only solution that lets teams share and manage overlay screens.", stat: "$10K+ hardware" },
          ].map((item, i) => (
            <div key={i} className="card-glass p-5 flex gap-5">
              <div className="flex-shrink-0 w-20 h-20 rounded-xl bg-red-500/10 flex flex-col items-center justify-center">
                <span className="text-red-400 font-bold text-sm text-center leading-tight">{item.stat}</span>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-[#E8E4DC] text-sm">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <h2 className="font-heading text-2xl font-semibold text-white mt-10 mb-4">Who Suffers Most?</h2>
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {[
            { role: "Remote Workers", pain: "Juggling video calls, documents, and chat simultaneously" },
            { role: "Day Traders", pain: "Need live charts visible while executing trades on other platforms" },
            { role: "Students", pain: "Following video lectures while taking notes in another app" },
            { role: "Developers", pain: "Referencing documentation or tutorials while writing code" },
            { role: "Content Creators", pain: "Monitoring chat and analytics while streaming or editing" },
            { role: "IT Operations", pain: "Watching dashboards and alerts while troubleshooting systems" },
          ].map((item, i) => (
            <div key={i} className="card-glass p-4">
              <h3 className="text-[#A3E635] font-semibold text-sm mb-1">{item.role}</h3>
              <p className="text-[#E8E4DC] text-sm">{item.pain}</p>
            </div>
          ))}
        </div>

        <h2 className="font-heading text-2xl font-semibold text-white mt-10 mb-4">The Market Gap</h2>
        <p className="text-[#E8E4DC] mb-4">
          There is no comprehensive solution that allows users to keep <strong className="text-white">ANY content</strong> visible 
          at all times, with full customization (frames, opacity, size, position), across all their devices 
          (browser, desktop, mobile), with team collaboration — all for a one-time affordable price.
        </p>
        <p className="text-[#E8E4DC]">
          <strong className="text-[#A3E635]">Until now.</strong> Top Screen&#8482; fills this gap completely.
        </p>
      </div>
    </ContentPage>
  );
}
